/**
 * Client-side Error Tracking
 *
 * Use this module in client components and browser code.
 * Initializes with appropriate provider based on configuration.
 */

import type {
  ErrorTrackingProvider,
  ProviderConfig,
  ErrorContext,
  LogLevel,
} from './types';
import { ConsoleProvider } from './providers/console';
import { RollbarProvider } from './providers/rollbar';

export type { ErrorContext, LogLevel, ProviderConfig } from './types';

let provider: ErrorTrackingProvider | null = null;

export type ProviderType = 'rollbar' | 'console';

interface InitOptions extends ProviderConfig {
  provider?: ProviderType;
}

/**
 * Initialize the error tracking client
 * Call this once in your app's entry point (e.g., _app.tsx or layout.tsx)
 */
export function initErrorTracking(options: InitOptions): void {
  const providerType = options.provider || 'rollbar';

  switch (providerType) {
    case 'rollbar':
      provider = new RollbarProvider();
      break;
    case 'console':
    default:
      provider = new ConsoleProvider();
      break;
  }

  provider.init(options);
}

/**
 * Capture an error
 */
export function captureError(
  error: Error | string,
  context?: ErrorContext
): void {
  if (!provider) {
    console.error('[ErrorTracking] Not initialized. Call initErrorTracking first.');
    console.error(error);
    return;
  }
  provider.captureError(error, context);
}

/**
 * Capture a message
 */
export function captureMessage(
  message: string,
  level: LogLevel = 'info',
  context?: ErrorContext
): void {
  if (!provider) {
    console.log(`[ErrorTracking] ${level}: ${message}`);
    return;
  }
  provider.captureMessage(message, level, context);
}

/**
 * Capture an API error with route context
 */
export function captureAPIError(
  error: Error | string,
  route: string,
  method: string,
  statusCode?: number
): void {
  captureError(error, {
    route,
    method,
    statusCode,
    tags: { type: 'api-error' },
  });
}

/**
 * Capture a form submission error
 */
export function captureFormError(
  error: Error | string,
  formName: string,
  fields?: string[]
): void {
  captureError(error, {
    extra: { formName, fields },
    tags: { type: 'form-error' },
  });
}

/**
 * Set user context (use non-PII identifiers only)
 */
export function setUser(userId: string | null): void {
  if (!provider) return;
  provider.setUser(userId);
}

/**
 * Add a breadcrumb for debugging context
 */
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>
): void {
  if (!provider) return;
  provider.addBreadcrumb(message, category, data);
}

/**
 * Check if error tracking is active
 */
export function isErrorTrackingActive(): boolean {
  return provider?.isActive() ?? false;
}
